import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Check, Sparkles, Loader2 } from "lucide-react";

interface Sport {
  id: string;
  name: string;
  icon: string | null;
}

interface VoiceOption {
  id: string;
  name: string;
  description: string | null;
  preview_audio_url: string | null;
}

interface ChatMessage {
  id: string;
  role: "coach" | "user";
  content: string;
}

const experienceLevels = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "elite", label: "Elite / Pro" },
];

const trainingFrequencies = [
  { value: "1-2", label: "1–2 days/week" },
  { value: "3-4", label: "3–4 days/week" },
  { value: "5-6", label: "5–6 days/week" },
  { value: "daily", label: "Every day" },
];

const mentalChallengeOptions = [
  "Pre-game anxiety",
  "Focus under pressure",
  "Confidence",
  "Recovery from setbacks",
  "Consistency",
  "Motivation",
  "Fear of failure",
  "Overthinking",
];

const goalOptions = [
  "Perform better in competition",
  "Build confidence",
  "Manage pressure",
  "Recover faster mentally",
  "Improve focus",
  "Develop consistency",
  "Stay motivated",
  "Handle setbacks",
];

type OnboardingStep = "sport" | "experience" | "frequency" | "challenges" | "goals" | "voice" | "complete";

const STEP_ORDER: OnboardingStep[] = ["sport", "experience", "frequency", "challenges", "goals", "voice", "complete"];

const COACH_QUESTIONS: Record<OnboardingStep, string> = {
  sport: "First — what sport do you play?",
  experience: "Got it. How would you describe your experience level?",
  frequency: "And how often do you train each week?",
  challenges: "What mental challenges come up most for you? Select all that apply.",
  goals: "Last one — what are your main goals with mental training? Pick as many as you'd like.",
  voice: "Almost done! Pick a voice for your visualizations. You can always change this later.",
  complete: "That's everything I need. Let me set up your personalized training space...",
};

const Onboarding = () => {
  const { user, refreshOnboarding } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sports, setSports] = useState<Sport[]>([]);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Form state
  const [sportId, setSportId] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [trainingFrequency, setTrainingFrequency] = useState("");
  const [mentalChallenges, setMentalChallenges] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [voiceId, setVoiceId] = useState("");

  // Pending multi-select
  const [pendingMulti, setPendingMulti] = useState<string[]>([]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [sportsRes, voicesRes] = await Promise.all([
        supabase.from("sports").select("*").eq("is_active", true).order("name"),
        supabase.from("voice_options").select("id, name, description, preview_audio_url").eq("is_active", true).order("sort_order"),
      ]);

      if (sportsRes.data) setSports(sportsRes.data);
      if (voicesRes.data) setVoices(voicesRes.data);
    };
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Initialize
  useEffect(() => {
    setMessages([
      { id: "intro", role: "coach", content: "Hey! I'm your Sports Performance Coach. Let's get your profile set up so I can create visualizations that are truly personalized to you. This'll only take a minute." },
    ]);
    setTimeout(() => showStep("sport"), 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showStep = (step: OnboardingStep) => {
    setIsTyping(true);
    scrollToBottom();
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: `q-${step}`, role: "coach", content: COACH_QUESTIONS[step] },
      ]);
      setCurrentStep(step);
      setIsTyping(false);
      setPendingMulti([]);
      scrollToBottom();
    }, 700 + Math.random() * 400);
  };

  const advanceToNext = (currentStepName: OnboardingStep, userMessage: string) => {
    // Add user message
    setMessages((prev) => [
      ...prev,
      { id: `a-${currentStepName}`, role: "user", content: userMessage },
    ]);
    scrollToBottom();

    const currentIndex = STEP_ORDER.indexOf(currentStepName);
    const nextStep = STEP_ORDER[currentIndex + 1];

    if (nextStep === "complete") {
      showStep("complete");
    } else if (nextStep) {
      showStep(nextStep);
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          sport_id: sportId || null,
          experience_level: experienceLevel,
          training_frequency: trainingFrequency,
          mental_challenges: mentalChallenges,
          goals,
          preferred_voice_id: voiceId || null,
          onboarding_completed: true,
        })
        .eq("user_id", user.id);

      if (error) throw error;
      await refreshOnboarding();
      navigate("/subscribe", { replace: true });
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  // Auto-complete after "complete" step is shown
  useEffect(() => {
    if (currentStep === "complete") {
      const timer = setTimeout(() => handleComplete(), 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const stepIndex = currentStep ? STEP_ORDER.indexOf(currentStep) : 0;
  const progress = ((stepIndex + 1) / STEP_ORDER.length) * 100;

  const renderInputArea = () => {
    if (!currentStep || currentStep === "complete" || isTyping) return null;

    switch (currentStep) {
      case "sport":
        return (
          <div className="flex flex-wrap justify-center gap-2 max-h-48 overflow-y-auto">
            {sports.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setSportId(s.id);
                  advanceToNext("sport", s.name);
                }}
                className="px-4 py-2.5 rounded-xl border border-border/50 bg-secondary/30 text-sm text-foreground hover:border-primary/50 hover:bg-primary/10 transition-all"
              >
                {s.name}
              </button>
            ))}
          </div>
        );

      case "experience":
        return (
            <div className="flex flex-wrap justify-center gap-2">
            {experienceLevels.map((l) => (
              <button
                key={l.value}
                onClick={() => {
                  setExperienceLevel(l.value);
                  advanceToNext("experience", l.label);
                }}
                className="px-4 py-2.5 rounded-xl border border-border/50 bg-secondary/30 text-sm text-foreground hover:border-primary/50 hover:bg-primary/10 transition-all"
              >
                {l.label}
              </button>
            ))}
          </div>
        );

      case "frequency":
        return (
            <div className="flex flex-wrap justify-center gap-2">
            {trainingFrequencies.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setTrainingFrequency(f.value);
                  advanceToNext("frequency", f.label);
                }}
                className="px-4 py-2.5 rounded-xl border border-border/50 bg-secondary/30 text-sm text-foreground hover:border-primary/50 hover:bg-primary/10 transition-all"
              >
                {f.label}
              </button>
            ))}
          </div>
        );

      case "challenges": {
        return (
          <div className="space-y-3">
              <div className="flex flex-wrap justify-center gap-2">
              {mentalChallengeOptions.map((c) => {
                const isSelected = pendingMulti.includes(c);
                return (
                  <button
                    key={c}
                    onClick={() => {
                      setPendingMulti((prev) =>
                        isSelected ? prev.filter((x) => x !== c) : [...prev, c]
                      );
                    }}
                    className={`px-4 py-2.5 rounded-xl border text-sm flex items-center gap-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    {c}
                  </button>
                );
              })}
            </div>
            {pendingMulti.length > 0 && (
              <Button
                size="sm"
                variant="hero"
                onClick={() => {
                  setMentalChallenges(pendingMulti);
                  advanceToNext("challenges", pendingMulti.join(", "));
                }}
                className="rounded-xl"
              >
                Confirm
                <Send className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        );
      }

      case "goals": {
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap justify-center gap-2">
              {goalOptions.map((g) => {
                const isSelected = pendingMulti.includes(g);
                return (
                  <button
                    key={g}
                    onClick={() => {
                      setPendingMulti((prev) =>
                        isSelected ? prev.filter((x) => x !== g) : [...prev, g]
                      );
                    }}
                    className={`px-4 py-2.5 rounded-xl border text-sm flex items-center gap-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    {g}
                  </button>
                );
              })}
            </div>
            {pendingMulti.length > 0 && (
              <Button
                size="sm"
                variant="hero"
                onClick={() => {
                  setGoals(pendingMulti);
                  advanceToNext("goals", pendingMulti.join(", "));
                }}
                className="rounded-xl"
              >
                Confirm
                <Send className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        );
      }

      case "voice": {
        const selectVoice = (voice: VoiceOption) => {
          setSelectedVoiceId(voice.id);

          // Stop any currently playing audio
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
          }
          setPlayingVoiceId(null);

          // Auto-play preview
          if (voice.preview_audio_url) {
            setPlayingVoiceId(voice.id);
            const audio = new Audio(voice.preview_audio_url);
            audio.onended = () => setPlayingVoiceId(null);
            audio.play().catch(() => {
              setPlayingVoiceId(null);
              toast.error("Failed to play voice preview");
            });
            audioRef.current = audio;
          }
        };

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg mx-auto">
              {voices.map((v) => {
                const isSelected = selectedVoiceId === v.id;
                const isPlaying = playingVoiceId === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => selectVoice(v)}
                    className={`px-4 py-3 rounded-xl border text-sm transition-all flex items-center justify-center gap-2 ${
                      isSelected
                        ? "border-primary bg-primary/10 text-foreground ring-1 ring-primary/50"
                        : "border-border/50 bg-secondary/30 text-foreground hover:border-primary/50 hover:bg-primary/10"
                    }`}
                  >
                    {isPlaying ? "🔊" : "▶"} {v.name}
                  </button>
                );
              })}
            </div>
            {selectedVoiceId && (
              <div className="flex justify-center">
                <Button
                  size="sm"
                  variant="hero"
                  onClick={() => {
                    setVoiceId(selectedVoiceId);
                    if (audioRef.current) audioRef.current.pause();
                    const selectedVoice = voices.find((v) => v.id === selectedVoiceId);
                    setPlayingVoiceId(null);
                    setSelectedVoiceId(null);
                    advanceToNext("voice", selectedVoice?.name ?? "Selected voice");
                  }}
                  className="rounded-xl"
                >
                  Choose this voice
                  <Send className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6 flex items-center gap-4 border-b border-border/30">
        <div className="w-8 h-8 rounded-full gradient-cta flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-primary-foreground" />
        </div>
        <div className="flex-1">
          <h1 className="font-display text-lg font-semibold">Sports Performance Coach</h1>
          <p className="text-xs text-muted-foreground">Setting up your profile</p>
        </div>
        <div className="w-24 h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full gradient-cta transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {/* Chat messages + inline input */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "coach" && (
                  <div className="w-8 h-8 rounded-full gradient-cta flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === "coach"
                      ? "bg-secondary/50 border border-border/30 text-foreground"
                      : "gradient-cta text-primary-foreground"
                  }`}
                >
                  {msg.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="w-8 h-8 rounded-full gradient-cta flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="bg-secondary/50 border border-border/30 rounded-2xl px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}

          {/* Saving indicator */}
          {saving && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-center"
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Setting up your profile...
              </div>
            </motion.div>
          )}

          {/* Inline input area — right after messages */}
          {renderInputArea() && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="pt-2 pb-8"
            >
              {renderInputArea()}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
